import { type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import AnimatedCheckCircle from '../../components/shared/AnimatedCheckCircle';
import ConfirmPopup from '../../components/shared/ConfirmPopup';
import history from '../../browserHistory';
import WarningIcon from '../../images/icons/Warning';
import { Colors } from '../../themes/colors';

interface ICustomPortalProps {
  /** The title text displayed in the popup */
  title?: string;
  /** The main descriptive text content */
  text?: string;
  /** Route to navigate to after dismissing popup. Use 'back' for history.back() */
  route?: string;
  /** Parameters to pass with route navigation */
  params?: Record<string, unknown>;
  /** Callback function executed when submit/ok button is pressed */
  submitPressed: () => void;
  /** Shows success state with animated check circle */
  isSuccess?: boolean;
  /** Shows error state with warning icon */
  isError?: boolean;
  /** Additional props passed to underlying ConfirmPopup component */
  popupProps?: Record<string, unknown>;
  /** Text for the confirmation button */
  buttonText?: string;
  /** Whether to perform navigation after button press */
  redirect?: boolean;
  /** Data-cy attribute for testing */
  dataCy?: string;
  /** Data-cy attribute for close popup testing */
  closePopupDataCy?: string;
  /** Data-cy attribute for right button testing */
  rightButtonDataCy?: string;
  /** Data-cy attribute for left button testing */
  leftButtonDataCy?: string;
}

export const CustomPortal = ({
  title,
  text,
  route,
  params,
  submitPressed,
  isSuccess = false,
  isError = false,
  popupProps = {},
  buttonText = 'Ok',
  redirect = true,
  dataCy,
  closePopupDataCy,
  rightButtonDataCy,
  leftButtonDataCy,
}: ICustomPortalProps) => {
  const navigate = useNavigate();

  const handleRightButtonAction = (): void => {
    submitPressed();

    if (!redirect) {
      return;
    }

    if (route === 'back') {
      navigate(-1);
    } else if (route) {
      history.push(route, params);
    }
  };

  const renderIcon = (): ReactNode => {
    if (isSuccess) {
      return <AnimatedCheckCircle />;
    }
    if (isError) {
      return <WarningIcon color={Colors.warning_500} width={70} height={70} />;
    }
    return null;
  };

  return (
    <ConfirmPopup
      Icon={renderIcon}
      dataCy={dataCy}
      title={title}
      titleStyle={{ marginTop: isSuccess || isError ? 30 : 0 }}
      description={text}
      descriptionStyle={{ width: '90%', marginBottom: 50 }}
      rightButtonText={buttonText}
      rightButtonAction={handleRightButtonAction}
      closePopupDataCy={closePopupDataCy}
      rightButtonDataCy={rightButtonDataCy}
      leftButtonDataCy={leftButtonDataCy}
      {...popupProps}
    />
  );
};
